/**
 * @fileoverview
 * This file serves as the central registry for all report hooks used throughout the app.
 *
 * For each report, define and export a custom React hook by calling the `createReport` function
 * from `./util` with the appropriate parameter type and report type string.
 *
 * Components can then import and use these hooks to generate reports with type safety and
 * consistent API usage.
 */
import { createReport } from './util';

export { useSendReportEmail } from './email';

export const useShippingAbcFormPartDReport = createReport<{
  embryoIds: string[];
  country?: string | null;
}>('shippingAbcFormPartD');

export const useShippingAbcFormPartDExcelReport = createReport<{
  embryoIds: string[];
  country?: string | null;
}>('shippingAbcFormPartDExcel');

export const useEmbryoCollectionReport = createReport<{
  accountCode: string;
  shipmentCode: string;
}>('embryoCollectionForm');

export const useFreshShipmentFormReport = createReport<{
  accountCode: string;
  shipmentCode: string;
}>('freshShipmentForm');

export const useExportSealFormReport = createReport<{
  accountCode: string;
  shipmentCode: string;
  freshShipmentId: string;
}>('exportSealForm', { detailedErrors: true });

export const useExportSealLabFormReport = createReport<{
  accountCode: string;
  shipmentCode: string;
  freshShipmentId: string;
}>('exportSealLabForm', { detailedErrors: true });

export const useShippingForm = createReport<{
  shipmentIds: string[];
  showPedigree?: boolean;
}>('shippingForm');

export const useShippingFormTerminalBeef = createReport<{
  shipmentId: string;
  showPedigree?: boolean;
}>('shippingFormTerminalBeef');

export const useShippingTripForm = createReport<{
  shipmentId: string;
  transferredOnly?: boolean;
}>('shippingTripForm');

export const useDairyCompPulseExportReport = createReport<{
  embryoIds: string[];
}>('DairyCompPulseExportReport');

export const useGenericGridExport = createReport<{
  gridData: unknown;
  reportName?: string;
}>('genericGridExport');

export const useFertWorkLabelsReport = createReport<{
  sessionId: string;
  matingsPerPlate?: number;
}>('fertWorkLabels');

export const useFertWorkSingleLabelReport = createReport<{
  sessionId: string;
  labelId: string;
  matingsPerPlate?: number;
}>('fertWorkSingleLabel');

export const useFertWorkSOFLabelsReport = createReport<{
  sessionId: string;
  incubatorName?: string;
  matingsPerPlate?: number;
}>('fertWorkSOFLabels');

export const useFertWorkMapsReport = createReport<{
  sessionId: string;
  matingsPerPlate?: number;
}>('fertWorkMaps');

export const useFertWorkSingleMapReport = createReport<{
  sessionId: string;
  incubatorName: string;
  matingsPerPlate?: number;
}>('fertWorkSingleMap');

export const useFertWorkRunSheetReport = createReport<{
  sessionId: string;
  runId?: string;
}>('fertWorkRunSheet');

export const useDonorSchedulePdfReport = createReport<{
  soScheduleIds: string[];
}>('donorSchedulePdf');

export const useDonorScheduleExcelReport = createReport<{
  soScheduleIds: string[];
}>('donorScheduleExcel');

export const useDonorToDoListPdfReport = createReport<{
  startDate: string;
  endDate: string;
  siteIds: string[];
  donorIds: string[];
  clinicIds: number[];
  accountIds: string[];
  yardIds: string[];
}>('donorToDoListPdf');

export const useDonorToDoListExcelReport = createReport<{
  startDate: string;
  endDate: string;
  siteIds: string[];
  donorIds: string[];
  clinicIds: number[];
  accountIds: string[];
  yardIds: string[];
}>('donorToDoListExcel');

export const useBullLabelsReport = createReport<{
  fertilizationSiteId: string;
  donorScheduleDate: string;
  extraLabels?: number | undefined;
  extraSemenLabels?:
    | {
        sireId: string | null;
        semenId: string | null;
        semenType: number | null;
        accountId: string | null;
        extraLabels: number;
      }[]
    | undefined;
}>('bullLabelsReport');

export const useFreezeTemplateReport = createReport<{
  fertilizationSiteId: string;
  fertilizationDate: string;
  /** 'v2' prints real discards (Freeze V2). Omit for the original template (Freeze V1). */
  variant?: 'v2';
}>('freezeTemplate');

export const useFreezeValidationReport = createReport<{
  fertilizationIds: string[];
}>('freezeValidation');

export const useEmbryoSummaryReport = createReport<{
  fertilizationSiteId: string;
  fertilizationDate: string;
  storageLocationId?: string;
}>('embryoSummary');

export const useEmbryoInventoryByEmbryoReport = createReport<{
  embryoIds: string[];
}>('embryoInventoryByEmbryo');

export const useSelectedSemenInventoryReport = createReport<{
  semenIds: string[];
}>('selectedSemenInventoryReport');

export const useFrozenEmbryoThawAndImplantReport = createReport<{
  embryoIds: string[];
  implantDate?: string;
  siteId?: string;
}>('frozenEmbryoThawAndImplant');

export const useFreezeGobletLabelsReport = createReport<{
  fertilizationIds: string[];
}>('freezeGobletLabels');

export const useFreezeStrawLabelsReport = createReport<{
  fertilizationIds: string[];
}>('freezeStrawLabels');

export const useFreezeCaneTabLabelsReport = createReport<{
  fertilizationIds: string[];
}>('freezeCaneTabLabels');

export const useFreezeLabelsReport = createReport<{
  fertilizationDate: string;
  fertilizationSiteId: string;
}>('freezeLabels');

export const useFreezeRunSheetReport = createReport<{
  fertilizationSiteId: string;
  fertilizationDate: string;
}>('freezeRunSheet');

export const useFreezeVitRunSheetReport = createReport<{
  fertilizationSiteId: string;
  fertilizationDate: string;
}>('freezeVitRunSheet');

export const useOocyteCollectionFormReport = createReport<{
  aspirationIds: string[];
  opuDate?: string;
  opuSiteId?: string;
  location?: string | null;
}>('oocyteCollectionForm');

export const useAspirationFormReport = createReport<{
  aspirationIds: string[];
}>('aspirationForm');

export type ShippingFormExportOptions = {
  title: string;
  showSoldTo: boolean;
  showStrawDisclaimer: boolean;
  seal: string;
  date: string;
  country: string;
  exporter: string;
  veterinarian: string;
  euEmbryoStatement: '' | 'Emb-A' | 'Emb-B' | 'Emb-D';
  euSemenStatement: boolean;
};

type ShippingFormExportParameters = ShippingFormExportOptions & {
  shipmentIds: string[];
};

export const useShippingFormExport =
  createReport<ShippingFormExportParameters>('shippingFormExport');

export const useShippingFormExportExcel =
  createReport<ShippingFormExportParameters>('shippingFormExportExcel');
